/**
 * Contextual "create task" button — dropped onto a specific object (a package header, a finding row, a bid
 * row) and pre-fills that object as the task's context + deep-link. Opens the same DelegateTaskModal as the
 * floating button. Captures the current screen so the assignee gets the visual context too. Internal-staff
 * gating is the caller's responsibility (pass-through); disabled-with-explanation while the API is gated.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { captureViewport } from '../lib/screenshot';
import { currentViewTab, currentPageUrl } from '../lib/captureContext';
import { useTaskConfigured, TASK_DISABLED_TITLE } from '../lib/useTaskConfigured';
import type { TaskContext } from '../lib/api';
import DelegateTaskModal from './DelegateTaskModal';

interface Props {
  context: TaskContext;
  /** Deep-link target for this object; defaults to the current page. */
  pageUrl?: string;
  packageId?: string;
  prefillTitle?: string;
  compact?: boolean;
}

export default function CreateTaskHere({ context, pageUrl, packageId, prefillTitle, compact }: Props) {
  const loc = useLocation();
  const { isInternal, configured } = useTaskConfigured();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [shot, setShot] = useState<string | null>(null);

  if (!isInternal) return null;

  const target = pageUrl ?? currentPageUrl(loc);
  const onClick = async () => {
    if (!configured) return;
    setCapturing(true);
    setShot(await captureViewport());
    setCapturing(false);
    setOpen(true);
  };

  return (
    <>
      <button type="button" onClick={onClick} disabled={!configured || capturing}
        className="small" title={configured ? '为此项派发任务 / Delegate a task for this' : TASK_DISABLED_TITLE}
        style={{ cursor: configured ? (capturing ? 'progress' : 'pointer') : 'not-allowed', opacity: configured ? 1 : 0.6 }}>
        {compact ? '📋' : (capturing ? '📸 …' : '📋 派发任务 / Delegate')}
      </button>
      <DelegateTaskModal
        open={open}
        onClose={() => setOpen(false)}
        context={context}
        initialScreenshot={shot}
        pageUrl={target}
        viewTab={context.view_tab ?? currentViewTab(loc)}
        packageId={packageId}
        prefillTitle={prefillTitle}
      />
    </>
  );
}
